import { publicReviewReportPath, REVIEW_COPY, TURNSTILE_HEADER, type VisitorReportInput } from "@fusiqo/vozqi-shared";
import { useState } from "react";

export type ReviewReport = {
  sending: boolean;
  sent: boolean;
  /** What to say over the button, or null. */
  problem: string | null;
  /** A problem found on the press, before anything goes out. */
  refuse: (problem: string) => void;
  send: (input: VisitorReportInput, token: string | null) => void;
};

type Outcome = { sent: true } | { sent: false; problem: string };

/** The API's own words for a refusal it explains (400, 404, 409); anything else is «No pudimos enviar». */
async function outcomeOf(response: Response, widget: boolean): Promise<Outcome> {
  if (response.status === 204) return { sent: true };
  if (response.status === 403 && widget) return { sent: false, problem: REVIEW_COPY.reportCaptcha };
  if (widget && [400, 404, 409].includes(response.status)) {
    const body = (await response.json().catch(() => null)) as { error?: { message?: unknown } } | null;
    const message = body?.error?.message;
    if (typeof message === "string" && message !== "") return { sent: false, problem: message };
  }
  return { sent: false, problem: REVIEW_COPY.reportFailed };
}

/**
 * A visitor's report of a profile review (P3 Enmienda 4), through the
 * Worker's door on this origin, which forwards it with the visitor's address.
 * The token is spent at siteverify whether the report went in or not, so
 * every attempt that did not end sent asks the widget for a fresh one. A
 * build without a sitekey draws no widget and the API refuses: that is only
 * ever «No pudimos enviar».
 */
export function useReviewReport(slug: string, reviewId: string, widget: { on: boolean; reset: () => void }): ReviewReport {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const send = (input: VisitorReportInput, token: string | null): void => {
    if (sending) return;
    setSending(true);
    setProblem(null);
    fetch(publicReviewReportPath(slug, reviewId), {
      method: "POST",
      headers: { "Content-Type": "application/json", [TURNSTILE_HEADER]: token ?? "" },
      body: JSON.stringify(input),
    })
      .then((response) => outcomeOf(response, widget.on))
      .catch((): Outcome => ({ sent: false, problem: REVIEW_COPY.reportFailed }))
      .then((outcome) => {
        setSending(false);
        if (outcome.sent) {
          setSent(true);
          return;
        }
        setProblem(outcome.problem);
        widget.reset();
      });
  };

  return { sending, sent, problem, refuse: setProblem, send };
}
